import React, { useEffect, useState } from 'react';
import { adminListContacts, adminMarkContact, adminDeleteContact } from '../../lib/api';
import { Loader2, Mail, Trash2, Check, MapPin } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function AdminContacts() {
  const [contacts, setContacts] = useState(null);
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const load = () => adminListContacts().then(setContacts);
  useEffect(() => { load(); }, []);

  const handleSelect = async (c) => {
    setSelected(c);
    if (!c.read) {
      await adminMarkContact(c.id, true);
      load();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await adminDeleteContact(id);
    setSelected(null);
    toast({ title: 'Supprimé' });
    load();
  };

  if (!contacts) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  return (
    <div className="p-8 lg:p-10">
      <header className="mb-6">
        <h1 className="font-display font-extrabold text-3xl text-sky-950">Messages reçus</h1>
        <p className="text-slate-500 mt-1">{contacts.length} message{contacts.length > 1 ? 's' : ''} — {contacts.filter((c) => !c.read).length} non lu{contacts.filter((c) => !c.read).length > 1 ? 's' : ''}</p>
      </header>

      {contacts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sky-100 p-16 text-center">
          <Mail className="w-12 h-12 text-sky-200 mx-auto mb-3" />
          <p className="text-slate-500">Aucun message pour le moment.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-5">
          <div className="bg-white rounded-2xl border border-sky-100 divide-y divide-sky-50 max-h-[75vh] overflow-y-auto">
            {contacts.map((c) => (
              <button
                key={c.id}
                onClick={() => handleSelect(c)}
                className={`w-full text-left p-4 hover:bg-sky-50/50 transition-colors ${selected?.id === c.id ? 'bg-sky-50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-xs font-bold text-sky-700 flex-shrink-0">
                    {c.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sky-950 text-sm truncate">{c.name}</p>
                      {!c.read && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"></span>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.email}</p>
                    <p className="text-xs text-slate-400 mt-1 truncate">{c.message}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-sky-100 p-6">
            {selected ? (
              <>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-sky-950">{selected.name}</h2>
                    <a href={`mailto:${selected.email}`} className="text-sm text-sky-700 hover:underline">{selected.email}</a>
                    {selected.city && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{selected.city}</p>}
                  </div>
                  <button onClick={() => handleDelete(selected.id)} className="p-2 rounded-xl hover:bg-rose-50 text-rose-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3 mb-5 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold">{selected.subject}</span>
                  <span className="text-slate-500">{new Date(selected.createdAt).toLocaleString('fr-FR')}</span>
                  <span className="flex items-center gap-1 text-emerald-600 font-semibold"><Check className="w-3 h-3" />Lu</span>
                </div>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 leading-relaxed">{selected.message}</div>
                <a href={`mailto:${selected.email}?subject=Re: votre message`} className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-xl bg-sky-50 text-sky-700 font-semibold text-sm hover:bg-sky-100">
                  <Mail className="w-4 h-4" />Répondre par email
                </a>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400 text-sm">
                <Mail className="w-10 h-10 mb-2 text-sky-200" />
                Sélectionnez un message
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
